import { useInfiniteQuery } from '@tanstack/react-query';
import InfiniteScroll from 'react-infinite-scroll-component';
import api from '../lib/axios';
import MessageItem from './MessageItem';
import DayDivider from './DayDivider';
import { MessageResponse } from '../types';
import { endpoints } from '../constants/endpoints';

interface MessageListProps {
  visitorId: string;
}

export default function MessageList({ visitorId }: MessageListProps) {
  const fetchMessages = async ({ pageParam = 0 }) => {
    const limit = 20;
    const skip = pageParam * limit;
    const data = (await api.get(
      endpoints.chat.messages(visitorId, skip, limit),
    )) as MessageResponse[];
    return {
      data,
      nextPage: data.length === limit ? pageParam + 1 : undefined,
    };
  };

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['messages', visitorId],
    queryFn: fetchMessages,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });

  const messages = data?.pages.flatMap((page) => page.data) || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-gray-500">Loading messages...</p>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-sm text-gray-500">No messages yet</p>
      </div>
    );
  }

  return (
    <div
      id="messageScrollableDiv"
      className="flex-1 overflow-y-auto flex flex-col-reverse"
    >
      <InfiniteScroll
        dataLength={messages.length}
        next={fetchNextPage}
        style={{ display: 'flex', flexDirection: 'column-reverse' }}
        inverse={true}
        hasMore={hasNextPage}
        loader={
          <div className="p-4 text-center">
            <span className="text-sm text-gray-500">
              Loading older messages...
            </span>
          </div>
        }
        scrollableTarget="messageScrollableDiv"
      >
        <div className="p-4 flex flex-col-reverse">
          {messages.map((message) => (
            <div key={message.id}>
              {message.show_date_divider && (
                <DayDivider date={message.created_at} />
              )}
              <MessageItem message={message} />
            </div>
          ))}
        </div>
      </InfiniteScroll>
    </div>
  );
}
